export type SidebarNavContext = 'platform' | 'tenant' | 'bot';

const BOT_PATH_RE = /^\/bots\/([^/]+)\/(?:overview|editor|config)(?:\/|$)/;
const TENANT_PREFIX_RE = /^\/tenants\/([^/]+)(?:\/|$)/;
const TENANT_ANALYTICS_PATH_RE = /^\/analytics\/tenant\/([^/]+)/;

const ROLES_BOT_CONTEXT = new Set(['superadmin', 'admin', 'creator']);

export type ResolveSidebarContextInput = {
  pathname: string;
  search: string;
  userRole: string | null | undefined;
  userTenantId: string | null | undefined;
  /** Persisted tenant selection (e.g. after loading a bot). */
  selectedTenantId: string | null | undefined;
};

export type ResolvedSidebarContext = {
  context: SidebarNavContext;
  tenantId: string | null;
  botId: string | null;
};

function parseBotIdFromPath(pathname: string): string | null {
  const m = pathname.match(BOT_PATH_RE);
  return m ? m[1] : null;
}

/** Paths where `?botId=` pins **bot** sidebar context (see docs/CONTEXTUAL_SIDEBAR.md). */
const BOT_ID_QUERY_PATHS = new Set([
  '/analytics/bot',
  '/livechat',
  '/livechat/workspace',
  '/reporting',
]);

function parseBotIdFromSearch(pathname: string, search: string): string | null {
  if (!BOT_ID_QUERY_PATHS.has(pathname)) return null;
  try {
    const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    const id = q.get('botId');
    return id && id.length > 0 ? id : null;
  } catch {
    return null;
  }
}

/** Superadmin deep-links: `?tenantId=` on bot-scoped query routes. */
function parseTenantIdFromSearch(pathname: string, search: string): string | null {
  if (!BOT_ID_QUERY_PATHS.has(pathname)) return null;
  try {
    const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    const id = q.get('tenantId');
    return id && id.length > 0 ? id : null;
  } catch {
    return null;
  }
}

function parseTenantIdFromPath(pathname: string): string | null {
  const m = pathname.match(TENANT_PREFIX_RE);
  return m ? m[1] : null;
}

const TENANT_REPORTING_PATH_RE = /^\/tenants\/[^/]+\/reporting\/?$/;

function pathAllowsBotIdQueryForHeader(pathname: string): boolean {
  if (BOT_ID_QUERY_PATHS.has(pathname)) return true;
  return TENANT_REPORTING_PATH_RE.test(pathname);
}

/**
 * Bot key from the URL for studio chrome (e.g. header resolves display name via `getBot`):
 * `bots.id` UUID or public `bot_id` from `/bots/:id/(overview|editor|config)` or `?botId=` on
 * livechat, analytics, reporting (including `/tenants/:tenantId/reporting?botId=`).
 * Does not read Zustand or agent workspace.
 */
export function getBotIdentifierFromLocationForHeader(pathname: string, search: string): string | null {
  const path = (pathname || '').replace(/\/+$/, '') || '/';
  const fromPath = parseBotIdFromPath(path);
  if (fromPath) return fromPath;
  if (!pathAllowsBotIdQueryForHeader(path)) return null;
  try {
    const raw = search.startsWith('?') ? search.slice(1) : search;
    const id = new URLSearchParams(raw).get('botId')?.trim();
    return id && id.length > 0 ? id : null;
  } catch {
    return null;
  }
}

/**
 * Determines sidebar context and ids for link construction.
 * See docs/CONTEXTUAL_SIDEBAR.md.
 */
export function resolveSidebarContext(input: ResolveSidebarContextInput): ResolvedSidebarContext {
  const role = input.userRole?.toLowerCase() ?? '';
  const pathname = input.pathname || '';
  const search = input.search || '';

  const botIdFromPath = parseBotIdFromPath(pathname);
  const botIdFromQuery = parseBotIdFromSearch(pathname, search);
  const botId = botIdFromPath ?? botIdFromQuery;

  const tenantIdFromPath = parseTenantIdFromPath(pathname);
  const userTenantId = input.userTenantId?.trim() || null;
  const selectedTenantId = input.selectedTenantId?.trim() || null;

  const canUseBotContext = ROLES_BOT_CONTEXT.has(role);
  if (botId && canUseBotContext) {
    const tenantId =
      tenantIdFromPath ??
      parseTenantIdFromSearch(pathname, search) ??
      userTenantId ??
      selectedTenantId ??
      null;
    return { context: 'bot', tenantId, botId };
  }

  if (role === 'superadmin') {
    const tenantFromAnalyticsPath = pathname.match(TENANT_ANALYTICS_PATH_RE)?.[1] ?? null;
    if (tenantFromAnalyticsPath) {
      return { context: 'tenant', tenantId: tenantFromAnalyticsPath, botId: null };
    }
    if (tenantIdFromPath) {
      return { context: 'tenant', tenantId: tenantIdFromPath, botId: null };
    }
    return { context: 'platform', tenantId: null, botId: null };
  }

  // admin, creator, agent — tenant-scoped when they have a tenant
  if (userTenantId) {
    return { context: 'tenant', tenantId: userTenantId, botId: null };
  }

  return { context: 'platform', tenantId: null, botId: null };
}
